import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import translations from '../i18n/translations';

const LanguageContext = createContext();

// Languages the app has translations for. Any other locale falls back
// to English. Order matters here: navigator.language is matched against
// this list, exact first (e.g. 'pt-BR' → 'pt'), then the 2-letter prefix.
const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt', 'ja', 'ko', 'zh'];

function detectFromNavigator() {
  try {
    const langs = Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
    for (const raw of langs) {
      if (!raw) continue;
      const lower = String(raw).toLowerCase();
      const two = lower.slice(0, 2);
      if (SUPPORTED_LANGUAGES.includes(two)) return two;
    }
  } catch (_e) { /* SSR / privacy mode */ }
  return null;
}

// When the device's primary locale isn't one we translate (e.g.
// Italian), we quietly stay in English. This returns that raw locale
// tag so the UI can note *why* it's showing English — but only until
// the user makes an explicit language choice (once `plant_app_language`
// is stored we never nag again).
function detectUnsupportedLocale() {
  try {
    if (localStorage.getItem('plant_app_language')) return null;
    const raw = (Array.isArray(navigator.languages) && navigator.languages[0]) || navigator.language;
    if (!raw) return null;
    const two = String(raw).toLowerCase().slice(0, 2);
    if (two === 'en' || SUPPORTED_LANGUAGES.includes(two)) return null;
    return String(raw);
  } catch {
    return null;
  }
}

export function LanguageProvider({ children }) {
  // Initial language:
  //   1. Explicit user choice from localStorage wins.
  //   2. First visit → try navigator.language and fall back to English.
  //      When auto-detected to a non-English language we also flip a
  //      transient `autoDetected` flag so the app can offer a one-tap
  //      "switch back to English" chip.
  const [language, setLanguageState] = useState(() => {
    const stored = localStorage.getItem('plant_app_language');
    if (stored) return stored;
    const detected = detectFromNavigator();
    if (detected && detected !== 'en') {
      // Persist so we don't ask navigator on every reload.
      try { localStorage.setItem('plant_app_language', detected); } catch { /* quota */ }
      try { localStorage.setItem('plant_app_language_autodetected', '1'); } catch { /* quota */ }
      return detected;
    }
    return 'en';
  });
  const [autoDetected, setAutoDetected] = useState(
    () => localStorage.getItem('plant_app_language_autodetected') === '1'
  );
  // Raw device locale when it isn't supported (and the user hasn't
  // explicitly chosen yet). Cleared the moment they pick a language.
  const [unsupportedLocale, setUnsupportedLocale] = useState(detectUnsupportedLocale);

  const setLanguage = useCallback((code) => {
    localStorage.setItem('plant_app_language', code);
    // Any explicit user pick clears the auto-detected flag — we never
    // want to nag with the "switch back to English" chip once the user
    // has expressed a preference.
    try { localStorage.removeItem('plant_app_language_autodetected'); } catch { /* quota */ }
    setAutoDetected(false);
    setUnsupportedLocale(null);
    setLanguageState(code);
  }, []);

  const dismissAutoDetected = useCallback(() => {
    try { localStorage.removeItem('plant_app_language_autodetected'); } catch { /* quota */ }
    setAutoDetected(false);
  }, []);

  const t = useCallback((key) => {
    const lang = translations[language] || translations.en;
    return lang[key] || translations.en[key] || key;
  }, [language]);

  // Keep the <html lang> attribute in sync so screen readers announce
  // the right language pack.
  useEffect(() => {
    try { document.documentElement.setAttribute('lang', language); } catch { /* SSR */ }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, autoDetected, dismissAutoDetected, unsupportedLocale }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
